import React, { useState, useEffect } from 'react';
import {
  X,
  Building2,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Archive,
  PauseCircle,
  PlayCircle,
  AlertTriangle,
} from 'lucide-react';

interface Institution {
  id: string;
  name: string;
  status: string;
  subscriptionPlan: string | null;
}

interface EditInstitutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  institution: Institution | null;
}

const availableSolutions = [
  { id: 'academic', label: 'Gestion Academique', description: 'Filieres, cours, notes' },
  { id: 'finance', label: 'Gestion Financiere', description: 'Paiements, factures, bourses' },
  { id: 'marketing', label: 'Gestion Marketing', description: 'Prospects, campagnes' },
];

const statusOptions = [
  { value: 'ACTIVE', label: 'Actif', color: 'green' },
  { value: 'TRIAL', label: 'Essai', color: 'amber' },
  { value: 'SUSPENDED', label: 'Suspendu', color: 'red' },
  { value: 'INACTIVE', label: 'Archive', color: 'gray' },
];

export const EditInstitutionModal: React.FC<EditInstitutionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  institution,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    solutions: [] as string[],
    status: 'ACTIVE',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    if (institution) {
      const solutions = institution.subscriptionPlan
        ? institution.subscriptionPlan.split(',').map(s => s.trim().toLowerCase())
        : [];
      setFormData({
        name: institution.name,
        solutions,
        status: institution.status,
      });
    }
  }, [institution]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSolutionToggle = (solutionId: string) => {
    setFormData((prev) => ({
      ...prev,
      solutions: prev.solutions.includes(solutionId)
        ? prev.solutions.filter((s) => s !== solutionId)
        : [...prev.solutions, solutionId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Le nom de l'etablissement est requis");
      return;
    }

    if (formData.solutions.length === 0) {
      setError('Veuillez selectionner au moins une solution');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/${institution?.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: formData.name,
            subscriptionPlan: formData.solutions,
            status: formData.status,
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Erreur lors de la mise a jour');
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onSuccess();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchive = async () => {
    if (!institution) return;

    setActionLoading('archive');
    setError(null);

    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/${institution.id}/archive`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Erreur lors de l'archivage");
      }

      setShowArchiveConfirm(false);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onSuccess();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      setShowArchiveConfirm(false);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspend = async () => {
    if (!institution) return;

    setActionLoading('suspend');
    setError(null);

    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/${institution.id}/suspend`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reason: 'Facture impayee' }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Erreur lors de la suspension');
      }

      setShowSuspendConfirm(false);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onSuccess();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      setShowSuspendConfirm(false);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async () => {
    if (!institution) return;

    setActionLoading('reactivate');
    setError(null);

    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/${institution.id}/reactivate`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Erreur lors de la reactivation');
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onSuccess();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClose = () => {
    if (!isSubmitting && !actionLoading) {
      setError(null);
      setSuccess(false);
      setShowArchiveConfirm(false);
      setShowSuspendConfirm(false);
      onClose();
    }
  };

  if (!isOpen || !institution) return null;

  const isArchived = institution.status === 'INACTIVE';
  const isSuspended = institution.status === 'SUSPENDED';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleClose}>
      <div className="fixed inset-0 bg-black/50 transition-opacity" />

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div
          className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Building2 size={20} className="text-primary-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Modifier l'etablissement
                  </h2>
                  <p className="text-sm text-gray-500">{institution.name}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={isSubmitting || !!actionLoading}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Success State */}
          {success && (
            <div className="p-6">
              <div className="text-center py-12">
                <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Modifications enregistrees!
                </h3>
              </div>
            </div>
          )}

          {/* Archive Confirmation */}
          {showArchiveConfirm && (
            <div className="p-6">
              <div className="text-center py-8">
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle size={32} className="text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Archiver cet etablissement?
                </h3>
                <p className="text-gray-600 mb-6">
                  Cette action va desactiver tous les utilisateurs de cet etablissement.
                  Ils ne pourront plus se connecter jusqu'a la reactivation.
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setShowArchiveConfirm(false)}
                    disabled={actionLoading === 'archive'}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleArchive}
                    disabled={actionLoading === 'archive'}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center gap-2"
                  >
                    {actionLoading === 'archive' ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Archivage...
                      </>
                    ) : (
                      <>
                        <Archive size={16} />
                        Confirmer l'archivage
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Suspend Confirmation */}
          {showSuspendConfirm && (
            <div className="p-6">
              <div className="text-center py-8">
                <div className="h-16 w-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PauseCircle size={32} className="text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Suspendre cet etablissement?
                </h3>
                <p className="text-gray-600 mb-6">
                  Les utilisateurs verront un message de suspension lie au reglement financier.
                  Cette action est reversible.
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setShowSuspendConfirm(false)}
                    disabled={actionLoading === 'suspend'}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSuspend}
                    disabled={actionLoading === 'suspend'}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium flex items-center gap-2"
                  >
                    {actionLoading === 'suspend' ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Suspension...
                      </>
                    ) : (
                      <>
                        <PauseCircle size={16} />
                        Confirmer la suspension
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          {!success && !showArchiveConfirm && !showSuspendConfirm && (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Error Alert */}
              {error && (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertCircle size={20} className="flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {/* Status Alert for Archived/Suspended */}
              {(isArchived || isSuspended) && (
                <div className={`flex items-center justify-between gap-3 p-4 rounded-lg ${
                  isArchived ? 'bg-gray-100' : 'bg-amber-50'
                }`}>
                  <div className="flex items-center gap-3">
                    {isArchived ? (
                      <Archive size={20} className="text-gray-600" />
                    ) : (
                      <PauseCircle size={20} className="text-amber-600" />
                    )}
                    <p className={`text-sm font-medium ${
                      isArchived ? 'text-gray-700' : 'text-amber-700'
                    }`}>
                      {isArchived ? 'Cet etablissement est archive' : 'Cet etablissement est suspendu'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleReactivate}
                    disabled={!!actionLoading}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                  >
                    {actionLoading === 'reactivate' ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <PlayCircle size={14} />
                    )}
                    Reactiver
                  </button>
                </div>
              )}

              {/* Institution Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de l'Etablissement <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Solutions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Solutions Actives <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availableSolutions.map((solution) => (
                    <label
                      key={solution.id}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.solutions.includes(solution.id)
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.solutions.includes(solution.id)}
                        onChange={() => handleSolutionToggle(solution.id)}
                        className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div>
                        <p className="font-medium text-gray-900 text-sm">
                          {solution.label}
                        </p>
                        <p className="text-xs text-gray-500">{solution.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting || !!actionLoading}
                  className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !!actionLoading}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save size={20} />
                      Enregistrer
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
